import request from './request'

/** list / meta 均未返回标签时的兜底 */
export const DEFAULT_USER_ORDER_STAT_LABELS: Record<string, string> = {
  hmc_orders: 'HMC Orders',
  fu_orders: 'FU Orders',
  ad_orders: 'AD Orders',
  hmc: 'HMC Orders',
  fu: 'FU Orders',
  ad: 'AD Orders',
}

export interface UserOrderStats {
  hmc_orders?: number
  fu_orders?: number
  ad_orders?: number
  hmc_orders_label?: string
  fu_orders_label?: string
  ad_orders_label?: string
  hmc_orders_display?: string
  fu_orders_display?: string
  ad_orders_display?: string
  items?: UserOrderStatItem[]
  [key: string]: unknown
}

export interface UserOrderStatItem {
  label?: string
  count?: number
  display?: string
  text?: string
  key?: string
  stat_key?: string
  order_id?: string
  orderId?: string
}

export interface UserUpcomingService {
  order_id?: string
  plan_key?: string
  plan_name?: string
  plan_label?: string
  label?: string
  name?: string
  title?: string
  service_name?: string
  service_date?: string
  date?: string
  time_text?: string
  time_start?: string
  time_end?: string
  display?: string
  [key: string]: unknown
}

export interface UserHomeItem {
  home_id?: string
  address?: string
  /** 房屋类型，如 Detached / Semi-Detached */
  type?: string
  home_type?: string
  year_built?: string | number
  bedrooms?: string | number
  bathrooms?: string | number
  size?: string
  /** 后端房屋详情常用字段，如 "2000~2499 sqft" */
  size_label?: string
  plan_label?: string
  care_plan?: string
  care_plan_label?: string
  subscription_text?: string
  /** 后端房屋详情常用字段，如 "Essential Care" */
  subscribed_plan?: string
  subscribed_plan_key?: string
  subscription_id?: string
  is_subscribed?: boolean | number
  order_stats?: UserOrderStats | UserOrderStatItem[]
  order_stat_items?: UserOrderStatItem[]
  hmc_orders?: number
  fu_orders?: number
  ad_orders?: number
  hmc_order_id?: string
  fu_order_id?: string
  ad_order_id?: string
  no_orders_text?: string
  upcoming_service?: UserUpcomingService | string | null
  upcoming_none_text?: string
  upcoming_plan_label?: string
  upcoming_display?: string
}

export interface UserListItem {
  id: number
  user_no?: string
  user_id?: string
  home_id?: string
  name?: string
  phone_code?: string
  phone?: string
  mobile?: string
  registered_mobile?: string
  address?: string
  email?: string
  /** 订阅计划文案，如 Essential Care；空或 Not subscribed 表示未订阅 */
  plan_label?: string
  care_plan?: string
  care_plan_label?: string
  subscription_text?: string
  is_subscribed?: boolean | number
  status?: number
  status_text?: string
  order_stats?: UserOrderStats | UserOrderStatItem[]
  order_stat_items?: UserOrderStatItem[]
  no_orders_text?: string
  upcoming_service?: UserUpcomingService | string | null
  upcoming_none_text?: string
  upcoming_plan_label?: string
  upcoming_display?: string
  /** 同一账号下多套房源；有值时列表按 homes 渲染多层 */
  homes?: UserHomeItem[]
  create_time?: string
  update_time?: string
}

export interface Pagination {
  page: number
  page_size: number
  total: number
  total_pages: number
}

export interface UserListData {
  list: UserListItem[]
  pagination: Pagination
}

export interface UserListParams {
  page: number
  page_size: number
  search_type?: string
  keyword?: string
  /** 1 = Active */
  status?: number
}

export interface UserMetaData {
  search_types?: Array<{ value: string; label: string } | string>
  order_stat_labels?: Record<string, string>
  order_stat_types?: Array<{ value: string; label: string } | string>
  order_types?: Array<{ value: string; label: string } | string>
}

export const USER_LIST_STATUS_ACTIVE = 1

export interface UserSearchTypeOption {
  value: string
  label: string
}

function isUserMobileSearchType(value: string): boolean {
  const normalized = value.trim().toLowerCase()
  return normalized === 'mobile_no' || normalized === 'mobile' || normalized.includes('mobile')
}

/** 搜索框内展示的短标签（下拉选项仍用完整 label） */
export function getUserSearchTypeDisplayLabel(
  option: Pick<UserSearchTypeOption, 'value' | 'label'>,
): string {
  if (isUserMobileSearchType(option.value)) return 'Mobile No.'
  if (option.value === 'home_id') return 'Home ID'
  if (option.value === 'user_id') return 'User ID'
  if (option.value === 'address') return 'Address'
  return option.label
}

export const DEFAULT_USER_SEARCH_TYPES: UserSearchTypeOption[] = [
  { value: 'mobile_no', label: 'Registered Mobile No.' },
  { value: 'home_id', label: 'Home ID' },
  { value: 'user_id', label: 'User ID' },
  { value: 'address', label: 'Address' },
]

function normalizeUserSearchTypeOption(
  entry: { value: string; label: string } | string,
): UserSearchTypeOption | null {
  if (typeof entry === 'string') {
    const value = entry.trim()
    if (!value) return null
    const fallback = DEFAULT_USER_SEARCH_TYPES.find((o) => o.value === value)
    const label = isUserMobileSearchType(value)
      ? 'Registered Mobile No.'
      : (fallback?.label ?? value)
    return { value, label }
  }
  const value = String(entry.value ?? '').trim()
  const label = String(entry.label ?? '').trim()
  if (!value) return null
  if (isUserMobileSearchType(value)) {
    return { value, label: 'Registered Mobile No.' }
  }
  return { value, label: label || value }
}

export function buildUserSearchTypes(
  meta: UserMetaData | null | undefined,
): UserSearchTypeOption[] {
  const raw = meta?.search_types
  if (!Array.isArray(raw) || !raw.length) {
    return [...DEFAULT_USER_SEARCH_TYPES]
  }
  const options = raw
    .map(normalizeUserSearchTypeOption)
    .filter((o): o is UserSearchTypeOption => o != null)
  return options.length ? options : [...DEFAULT_USER_SEARCH_TYPES]
}

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

/** POST `/api/admin/user/meta` */
export async function postUserMeta(
  params: Partial<UserListParams> = {},
): Promise<ApiResponse<UserMetaData>> {
  return request.post<ApiResponse<UserMetaData>>('/api/admin/user/meta', params)
}

/** POST `/api/admin/user/list` */
export async function getUserList(params: UserListParams) {
  return request.post<ApiResponse<UserListData>>('/api/admin/user/list', params)
}

export async function searchUserList(params: UserListParams) {
  return getUserList(params)
}

/** sessionStorage：订单详情等跳转 Users 时预填搜索 */
export const USERS_LIST_PREFILL_KEY = 'usersListPrefill'

export interface UsersListPrefill {
  searchType: string
  keyword: string
}

export function writeUsersListPrefill(payload: UsersListPrefill): void {
  sessionStorage.setItem(USERS_LIST_PREFILL_KEY, JSON.stringify(payload))
}

export function readUsersListPrefill(): UsersListPrefill | null {
  try {
    const raw = sessionStorage.getItem(USERS_LIST_PREFILL_KEY)
    if (!raw) return null
    sessionStorage.removeItem(USERS_LIST_PREFILL_KEY)
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const o = parsed as UsersListPrefill
    const searchType = typeof o.searchType === 'string' ? o.searchType.trim() : ''
    const keyword = typeof o.keyword === 'string' ? o.keyword.trim() : ''
    if (!searchType || !keyword) return null
    return { searchType, keyword }
  } catch {
    return null
  }
}
